import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  MessageSquare, 
  Image, 
  Video, 
  CreditCard, 
  Settings, 
  Plus, 
  ChevronLeft,
  ChevronRight,
  Trash2,
  MoreHorizontal,
  Shield,
  Building2,
  Palette
} from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import AnimatedLogo from '../common/AnimatedLogo';
import { useAuth } from '../../context/AuthContext';

// Admin emails - add your email here to access admin dashboard
const ADMIN_EMAILS = ['admin@jafai.com', 'owner@jafai.com', 'nsgt09@gmail.com'];

const Sidebar = ({ conversations = [], onNewChat, onDeleteChat, isCollapsed, setIsCollapsed }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [hoveredConv, setHoveredConv] = useState(null);
  
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email);

  const navItems = [
    { icon: Building2, label: 'Design Studio', path: '/dashboard/images' },
    { icon: Palette, label: 'Mood Board', path: '/dashboard/moodboard' },
    { icon: MessageSquare, label: 'Chat', path: '/dashboard' },
    { icon: Video, label: 'Video Studio', path: '/dashboard/videos' },
    { icon: CreditCard, label: 'Subscription', path: '/dashboard/subscription' },
    { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
    // Admin link - only show for admin users
    ...(isAdmin ? [{ icon: Shield, label: 'Admin', path: '/dashboard/admin', isAdmin: true }] : []),
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {!isCollapsed && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsCollapsed(true)}
        />
      )}
      
      <div 
        className={cn(
          "h-screen bg-zinc-900 dark:bg-zinc-950 border-r border-zinc-800 flex flex-col transition-all duration-300 z-50 overflow-hidden",
          // Mobile: fixed position, completely hidden when collapsed
          "fixed lg:relative",
          isCollapsed 
            ? "-translate-x-full lg:translate-x-0 lg:w-16 w-0 opacity-0 lg:opacity-100" 
            : "w-64 translate-x-0 opacity-100"
        )}
      >
      {/* Header */}
      <div className="p-3 flex items-center justify-between border-b border-zinc-800">
        {!isCollapsed && (
          <AnimatedLogo size="sm" showText={true} />
        )}
        {isCollapsed && (
          <AnimatedLogo size="sm" showText={false} />
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={cn("h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-800", isCollapsed && "mx-auto")}
        >
          {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      {/* New Chat Button */}
      <div className="p-3">
        <Button
          onClick={onNewChat}
          className={cn(
            "w-full bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-500 hover:to-cyan-500 text-white border-0 transition-all",
            isCollapsed ? "px-0" : "justify-start gap-2"
          )}
        >
          <Plus className="h-4 w-4" />
          {!isCollapsed && "New Chat"}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="px-3 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link key={item.path} to={item.path}>
              <div
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-lg transition-all cursor-pointer",
                  isActive 
                    ? "bg-zinc-800 text-white" 
                    : "text-zinc-400 hover:text-white hover:bg-zinc-800/50",
                  isCollapsed && "justify-center px-0"
                )}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {!isCollapsed && <span className="text-sm">{item.label}</span>}
              </div>
            </Link>
          );
        })}
      </nav>

      {/* Conversations List */}
      {!isCollapsed && (
        <div className="flex-1 flex flex-col mt-4 min-h-0">
          <div className="px-3 py-2">
            <span className="text-xs text-zinc-500 uppercase tracking-wider">Recent Chats</span>
          </div>
          <ScrollArea className="flex-1 px-3">
            <div className="space-y-1 pb-4">
              {conversations.map((conv) => (
                <div
                  key={conv.id}
                  className="group relative"
                  onMouseEnter={() => setHoveredConv(conv.id)}
                  onMouseLeave={() => setHoveredConv(null)}
                >
                  <Link to={`/dashboard/chat/${conv.id}`}>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-all">
                      <MessageSquare className="h-4 w-4 flex-shrink-0" />
                      <span className="text-sm truncate flex-1">{conv.title}</span>
                    </div>
                  </Link>
                  {hoveredConv === conv.id && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                        <DropdownMenuItem 
                          onClick={() => onDeleteChat(conv.id)}
                          className="text-red-400 focus:text-red-400 focus:bg-red-400/10"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
    </>
  );
};

export default Sidebar;
